'use client';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { PatternFormat } from "react-number-format";
import {
    TextField,
    Box,
} from '@mui/material';
import emailjs from '@emailjs/browser';

import Footer from '@/components/Footer';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import { useState } from 'react';
import { toast } from 'react-toastify';
import Navbar from '@/components/Navbar';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const schema = z.object({
    nome: z.string().min(1, 'Nome é obrigatório'),
    sobrenome: z.string().min(1, 'Sobrenome é obrigatório'),
    celular: z.string().min(15, 'Celular inválido'),
    mensagem: z.string().min(10, 'Mensagem deve ter pelo menos 10 caracteres'),
});

type FormData = z.infer<typeof schema>;

export default function ContatoClient() {
    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(schema),
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = (data: FormData) => {
        setIsSubmitting(true);

        const templateParams = {
            nome: `${data.nome} ${data.sobrenome}`,
            celular: data.celular,
            message: data.mensagem,
        };

        emailjs.send(
            'service_ltl0579',
            'template_z61nnh9',
            templateParams,
            'n0MmDpvVOoshkFyem'
        )
            .then(() => {
                toast.success('Mensagem enviada com sucesso!');
                reset();
            }, () => {
                toast.error('Erro ao enviar a mensagem. Tente novamente mais tarde.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className='overflow-x-hidden pt-[100px] sm:pt-[70px]'>
            <div className='min-h-[85vh] flex flex-col'>
                <div className="2xl:max-w-[1500px] md:pt-20 pt-10 w-[100vw] mx-auto md:px-10 px-6 mb-10 flex flex-col flex-1">
                    <Navbar />

                    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-12 gap-0">
                        {/* Formulário - Lado Esquerdo */}
                        <div className="border-2 border-forbli-lime rounded-xl p-8">
                            <Box className='relative z-20' component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ my: 2 }}>
                                <div className='grid grid-cols-2 gap-4'>
                                    <div>
                                        <label className="block text-sm px-2 text-gray-700 mb-1">Nome *</label>
                                        <TextField
                                            fullWidth
                                            variant="outlined"
                                            {...register('nome')}
                                            size='small'
                                            error={!!errors.nome}
                                            sx={{
                                                backgroundColor: '#eaeaea',
                                                borderRadius: 8,
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        border: '2px solid #d3db42',
                                                        borderRadius: 8,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#000',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#000',
                                                    },
                                                },
                                            }}
                                        />
                                        {errors.nome && <span className="text-red-500 text-sm">
                                            <InfoOutlineIcon sx={{ fontSize: 14 }} /> Insira um nome.
                                        </span>}
                                    </div>
                                    <div>
                                        <label className="block text-sm px-2 text-gray-700 mb-1">Sobrenome *</label>
                                        <TextField
                                            fullWidth
                                            size='small'
                                            variant="outlined"
                                            {...register('sobrenome')}
                                            error={!!errors.sobrenome}
                                            sx={{
                                                backgroundColor: '#eaeaea',
                                                borderRadius: 8,
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        border: '2px solid #d3db42',
                                                        borderRadius: 8,
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#000',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#000',
                                                    },
                                                },
                                            }}
                                        />
                                        {errors.sobrenome && <span className="text-red-500 text-sm">
                                            <InfoOutlineIcon sx={{ fontSize: 14 }} /> Insira um sobrenome.
                                        </span>}
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <label className="block text-sm px-2 text-gray-700 mb-1">Telefone *</label>
                                    <Controller
                                        name="celular"
                                        control={control}
                                        render={({ field }) => (
                                            <>
                                                <PatternFormat
                                                    {...field}
                                                    customInput={TextField}
                                                    format="(##) #####-####"
                                                    mask="_"
                                                    fullWidth
                                                    size='small'
                                                    error={!!errors.celular}
                                                    onValueChange={(values) => {
                                                        field.onChange(values.value)
                                                    }}
                                                    sx={{
                                                        backgroundColor: '#eaeaea',
                                                        borderRadius: 8,
                                                        '& .MuiOutlinedInput-root': {
                                                            '& fieldset': {
                                                                border: '2px solid #d3db42',
                                                                borderRadius: 8,
                                                            },
                                                            '&:hover fieldset': {
                                                                borderColor: '#000',
                                                            },
                                                            '&.Mui-focused fieldset': {
                                                                borderColor: '#000',
                                                            },
                                                        },
                                                    }}
                                                />
                                                {errors.celular && <span className="text-red-500 text-sm">
                                                    <InfoOutlineIcon sx={{ fontSize: 14 }} /> Insira o número de telefone.
                                                </span>}
                                            </>
                                        )}
                                    />
                                </div>

                                <div className="mt-6">
                                    <label className="block text-sm px-2 text-gray-700 mb-1">Deixe sua mensagem *</label>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        {...register('mensagem')}
                                        error={!!errors.mensagem}
                                        size='small'
                                        placeholder="Dúvidas, elogios ou sugestões."
                                        sx={{
                                            backgroundColor: '#eaeaea',
                                            borderRadius: 8,
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    border: '2px solid #d3db42',
                                                    borderRadius: 8,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#000',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#000',
                                                },
                                            },
                                        }}
                                    />
                                    {errors.mensagem && <span className="text-red-500 text-sm">
                                        <InfoOutlineIcon sx={{ fontSize: 14 }} /> A mensagem deve ter pelo menos 10 caracteres.
                                    </span>}
                                </div>

                                <button disabled={isSubmitting} type="submit" className='rounded-full bg-forbli-lime text-white w-full py-3 mt-6 font-semibold'>
                                    {isSubmitting ? 'Enviando...' : 'Enviar'}
                                </button>
                            </Box>
                        </div>

                        {/* Informações de Contato - Lado Direito */}
                        <div className="">
                            <div className="space-y-8">
                                <div>
                                    <h1 className="md:text-6xl text-4xl mt-20 font-extrabold text-gray-900 mb-6">Entre em contato</h1>
                                    <p className="text-gray-600 max-w-xl">
                                        Tire suas dúvidas sobre os empreendimentos Park Europa em Barbacena e Park da Alterosa em São João del-Rei, financiamento Caixa e disponibilidade de unidades.
                                    </p>
                                </div>

                                <div className="space-y-6">
                                    <div className="flex items-start gap-2">
                                        <div className="w-6 h-6 mt-1">
                                            <LocationPinIcon />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-extralight text-gray-900 mb-1">Endereço</p>
                                            <p className="text-gray-600">Rua João Ribeiro da Fonseca, 122 - Barbacena - MG, 36202-278</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start gap-2">
                                        <div className="w-6 h-6 mt-1">
                                            <AccessTimeIcon/>
                                        </div>
                                        <div>
                                            <p className="text-2xl font-extralight text-gray-900 mb-1">Horários de atendimento</p>
                                            <p className="text-gray-600">Segunda - Sexta: 8h00 às 18h00</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-6 pl-8">
                                    <p className="text-2xl text-gray-700 font-extralight">Será um prazer te atender!</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='absolute z-0 flex flex-col items-center justify-center '>
                    <img src={'/images/common/icon-2.webp'} alt={'Ícone 3'} className="scale-y-[-1] scale-x-[-1] absolute right-16" width={520} height={208} />
                </div>
            </div>
            <Footer />
        </div>
    );
}
